import {
  apiErrorSchema,
  maintenanceProtocolAttachmentSchema,
  maintenanceProtocolArchiveDetailSchema,
  maintenanceProtocolEditorSchema,
  maintenanceProtocolSignatureSchema,
  maintenanceProtocolValidationResultSchema,
  type FinalizeMaintenanceWorkOrderRequest,
  type MaintenanceProtocolEditor,
  type UpdateMaintenanceProtocolNotesRequest,
  type UpsertMaintenanceProtocolAnswerRequest,
  type UpsertMaintenanceInkLayerRequest,
  updateMaintenanceProtocolNotesResponseSchema,
  upsertMaintenanceProtocolAnswerResponseSchema,
  upsertMaintenanceInkLayerResponseSchema,
  maintenanceInkLayerListSchema,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { API_BASE_URL, ApiRequestError, apiFetch } from '@/lib/api';

export const maintenanceProtocolKey = (id: string) => ['maintenance', 'protocol', id] as const;

export function useMaintenanceProtocol(protocolId: string) {
  return useQuery({
    queryKey: maintenanceProtocolKey(protocolId),
    queryFn: () =>
      apiFetch(`/maintenance/protocols/${protocolId}`, maintenanceProtocolEditorSchema),
    enabled: Boolean(protocolId),
  });
}

export function useMaintenanceProtocolArchiveDetail(protocolId: string) {
  return useQuery({
    queryKey: ['maintenance', 'protocol-archive-detail', protocolId] as const,
    queryFn: () =>
      apiFetch(
        `/maintenance/protocols/${protocolId}/detail`,
        maintenanceProtocolArchiveDetailSchema,
      ),
    enabled: Boolean(protocolId),
  });
}

export function useValidateMaintenanceProtocol(protocolId: string) {
  return useMutation({
    mutationFn: () =>
      apiFetch(
        `/maintenance/protocols/${protocolId}/validate`,
        maintenanceProtocolValidationResultSchema,
        { method: 'POST' },
      ),
  });
}

export function useUpsertMaintenanceProtocolAnswer(protocolId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      fieldKey,
      body,
    }: {
      fieldKey: string;
      body: UpsertMaintenanceProtocolAnswerRequest;
    }) =>
      apiFetch(
        `/maintenance/protocols/${protocolId}/answers/${fieldKey}`,
        upsertMaintenanceProtocolAnswerResponseSchema,
        {
          method: 'PUT',
          body: JSON.stringify(body),
        },
      ),
    onSuccess: (data) => {
      client.setQueryData<MaintenanceProtocolEditor>(maintenanceProtocolKey(protocolId), (prev) =>
        prev
          ? {
              ...prev,
              revision: data.revision,
              answers: [
                ...prev.answers.filter((a) => a.fieldKey !== data.answer.fieldKey),
                data.answer,
              ],
            }
          : prev,
      );
    },
  });
}

export function useUpdateMaintenanceProtocolNotes(protocolId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMaintenanceProtocolNotesRequest) =>
      apiFetch(
        `/maintenance/protocols/${protocolId}/notes`,
        updateMaintenanceProtocolNotesResponseSchema,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    onSuccess: (data) => {
      client.setQueryData<MaintenanceProtocolEditor>(maintenanceProtocolKey(protocolId), (prev) =>
        prev ? { ...prev, generalNotes: data.generalNotes } : prev,
      );
      void client.invalidateQueries({ queryKey: ['maintenance', 'work-order'] });
      void client.invalidateQueries({
        queryKey: ['maintenance', 'protocol-archive-detail', protocolId],
      });
    },
  });
}

export function useMaintenanceInkLayers(protocolId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['maintenance', 'protocol-ink', protocolId] as const,
    queryFn: () =>
      apiFetch(`/maintenance/protocols/${protocolId}/ink`, maintenanceInkLayerListSchema),
    enabled: Boolean(protocolId) && enabled,
  });
}

export function useUpsertMaintenanceInkLayer(protocolId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ page, body }: { page: number; body: UpsertMaintenanceInkLayerRequest }) =>
      apiFetch(
        `/maintenance/protocols/${protocolId}/ink/${page}`,
        upsertMaintenanceInkLayerResponseSchema,
        { method: 'PUT', body: JSON.stringify(body) },
      ),
    onSuccess: (data) => {
      client.setQueryData<MaintenanceProtocolEditor>(maintenanceProtocolKey(protocolId), (prev) =>
        prev ? { ...prev, revision: data.revision } : prev,
      );
      void client.invalidateQueries({ queryKey: ['maintenance', 'protocol-ink', protocolId] });
    },
  });
}

export async function uploadMaintenanceProtocolAttachment(
  protocolId: string,
  file: File,
  fieldKey?: string,
) {
  const form = new FormData();
  form.append('file', file);
  const query = fieldKey ? `?fieldKey=${encodeURIComponent(fieldKey)}` : '';
  const response = await fetch(
    `${API_BASE_URL}/maintenance/protocols/${protocolId}/attachments${query}`,
    {
      method: 'POST',
      body: form,
      credentials: 'include',
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(body);
    throw new ApiRequestError(
      parsed.success
        ? parsed.data
        : {
            statusCode: response.status,
            code: 'UNKNOWN',
            message: 'Anhang konnte nicht hochgeladen werden.',
            timestamp: new Date().toISOString(),
            path: `/maintenance/protocols/${protocolId}/attachments`,
          },
    );
  }
  return maintenanceProtocolAttachmentSchema.parse(body);
}

export async function uploadMaintenanceProtocolSignature(
  protocolId: string,
  file: File,
  input: {
    kind: 'technician' | 'customer';
    consentConfirmed: boolean;
    signerName?: string;
    signerRole?: string;
  },
) {
  const form = new FormData();
  form.append('file', file);
  const params = new URLSearchParams({
    kind: input.kind,
    consentConfirmed: String(input.consentConfirmed),
  });
  if (input.signerName) {
    params.set('signerName', input.signerName);
  }
  if (input.signerRole) {
    params.set('signerRole', input.signerRole);
  }
  const response = await fetch(
    `${API_BASE_URL}/maintenance/protocols/${protocolId}/signatures?${params.toString()}`,
    { method: 'POST', body: form, credentials: 'include' },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(body);
    throw new ApiRequestError(
      parsed.success
        ? parsed.data
        : {
            statusCode: response.status,
            code: 'UNKNOWN',
            message: 'Unterschrift konnte nicht gespeichert werden.',
            timestamp: new Date().toISOString(),
            path: `/maintenance/protocols/${protocolId}/signatures`,
          },
    );
  }
  return maintenanceProtocolSignatureSchema.parse(body);
}

export function useFinalizeMaintenanceWorkOrder(workOrderId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: FinalizeMaintenanceWorkOrderRequest) =>
      apiFetch(
        `/maintenance/work-orders/${workOrderId}/finalize`,
        z.object({
          workOrderId: z.string().uuid(),
          status: z.string(),
          revision: z.number(),
        }),
        {
          method: 'POST',
          body: JSON.stringify(body),
        },
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['maintenance'] });
    },
  });
}
